import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import { useUserStore } from "../context/UserContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function AdminSettings() {
  const { selectedUser } = useUserStore();
  
  const [guardRanks, setGuardRanks] = useState([]); // State for fetched ranks
  const [userName, setUserName] = useState("");
  const [gradeList, setGradeList] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingRanks, setFetchingRanks] = useState(true); // Loading state for ranks
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 991;

  // Editable Group Name
  const [groupName, setGroupName] = useState(
    `SEC-GRP-${Math.floor(1000 + Math.random() * 9000)}`
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  /* FETCH UNIQUE RANKS FROM API */
  useEffect(() => {
    const fetchGuardRanks = async () => {
      try {
        setFetchingRanks(true);
        const token = localStorage.getItem("userToken");
        
        const response = await axios.get(
          `${BASE_URL}/api/officer/unique-ranks`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        // Assuming the API returns an array of ranks
        // You might need to adjust based on your API response structure
        if (response.data && Array.isArray(response.data)) {
          setGuardRanks(response.data);
        } else if (response.data?.ranks) {
          setGuardRanks(response.data.ranks);
        } else if (response.data?.data) {
          setGuardRanks(response.data.data);
        } else {
          console.warn("Unexpected API response format:", response.data);
          setGuardRanks([]);
        }
      } catch (error) {
        console.error("Error fetching guard ranks:", error);
        toast.error("Failed to load ranks");
        setGuardRanks([]);
      } finally {
        setFetchingRanks(false);
      }
    };

    fetchGuardRanks();
  }, []); // Empty dependency array means this runs once on mount

  /* ADMIN NAME */
  useEffect(() => {
    if (selectedUser?.userName) {
      setUserName(selectedUser.userName);
      return;
    }

    const token = localStorage.getItem("userToken");
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      setUserName(decoded?.name || decoded?.username || decoded?.email || "User");
    } catch (error) {
      console.error("Token decode error:", error);
      setUserName("User");
    }
  }, [selectedUser]);

  /* ADD RANK */
  const handleAddGrade = () => {
    if (!selectedGrade) {
      toast.error("Please select a rank first");
      return;
    }

    const alreadyExists = gradeList.find((g) => g.rank === selectedGrade);
    if (alreadyExists) {
      toast.error(`Rank ${selectedGrade} already added`);
      return;
    }

    setGradeList([...gradeList, { rank: selectedGrade, total: "" }]);
    setSelectedGrade("");
  };

  /* REMOVE ROW */
  const handleRemoveGrade = (index) => {
    const updated = [...gradeList];
    updated.splice(index, 1);
    setGradeList(updated);
  };

  /* INPUT CHANGE */
  const handleChange = (index, value) => {
    const updated = [...gradeList];
    updated[index].total = value;
    setGradeList(updated);
  };

  /* SUBMIT */
  const handleSubmit = async () => {
    if (gradeList.length === 0) {
      toast.error("Please add at least one rank");
      return;
    }

    for (let item of gradeList) {
      if (!item.total) {
        toast.error(`Enter Officer count for Rank ${item.rank}`);
        return;
      }
    }

    const payload = {
      name: groupName,
      values: gradeList.map((g) => ({
        rank: g.rank,
        value: g.total,
      })),
    };

    try {
      setLoading(true);
      const token = localStorage.getItem("userToken");

      await axios.post(`${BASE_URL}/api/setting/set`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      toast.success("Security Group Saved Successfully");
      setGradeList([]);
      setGroupName(`SEC-GRP-${Math.floor(1000 + Math.random() * 9000)}`);
    } catch (err) {
      console.error("Save Error:", err);
      toast.error("Failed to save settings ❌");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching ranks
  if (fetchingRanks) {
    return (
      <div className="settings-wrapper">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#e2e8f0'
        }}>
          <div style={{ textAlign: 'center' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>Loading ranks...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-wrapper">
      {/* Header */}
      <header className="settings-header">
        <div className="header-content">
          <div className="header-title">
            <h1>Create Security GroupS</h1>
            <p>Configure Security Groups & Ranks</p>
          </div>
          <div className="admin-info">
            Logged in as: <strong>{userName}</strong>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="settings-main">
        <div className="settings-card">
          <div className="settings-grid">
            {/* Left Panel - Rules & Info */}
            <div className="info-panel">
              <h3>
                <i className="fas fa-shield-alt"></i> Security Group Rules
              </h3>
              <ul className="rules-list">
                <li>All ranks are automatically fetched from the database.</li>
                <li>Duplicate ranks are strictly blocked to avoid data conflicts.</li>
                <li>All selected ranks are saved together under a single Security Group.</li>
                <li>Each Security Group must have a unique name for proper identification.</li>
                <li>
                  Example Group Names: <strong>SEC-GRP-1023</strong>, <strong>SEC-GRP-4589</strong>
                </li>
              </ul>

              <div className="group-code-box">
                <span>🔒 Group Code:</span>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  placeholder="e.g. SEC-GRP-1234"
                />
              </div>
            </div>

            {/* Right Panel - Rank Configuration */}
            <div className="config-panel">
              <div className="ranks-header">
                <p>
                  <i className="fas fa-list"></i> Available Ranks: <strong>{guardRanks.length}</strong>
                </p>
                {guardRanks.length === 0 && (
                  <p style={{ color: '#ef4444', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                    No ranks found. Please add officers with ranks first.
                  </p>
                )}
              </div>

              <div className="add-rank-bar">
                <select
                  value={selectedGrade}
                  onChange={(e) => setSelectedGrade(e.target.value)}
                  className="rank-select"
                  disabled={guardRanks.length === 0}
                >
                  <option value="">-- Select Rank --</option>
                  {guardRanks.map((r, i) => (
                    <option key={i} value={r}>
                      {r}
                    </option>
                  ))}
                </select>

                <button 
                  onClick={handleAddGrade} 
                  className="add-btn"
                  disabled={guardRanks.length === 0}
                >
                  <i className="fas fa-plus"></i> Add Rank
                </button>
              </div>

              {gradeList.length > 0 && (
                <>
                  <div className="selected-group">
                    <h4>
                      <i className="fas fa-lock"></i> Security Group: {groupName}
                    </h4>

                    <div className="grades-list">
                      {gradeList.map((item, index) => (
                        <div key={index} className="grade-row">
                          <div className="grade-badge">{item.rank}</div>
                          <input
                            type="number"
                            placeholder="Officer Count"
                            value={item.total}
                            onChange={(e) => handleChange(index, e.target.value)}
                            className="count-input"
                          />
                          <button
                            onClick={() => handleRemoveGrade(index)}
                            className="remove-btn"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="submit-btn"
                  >
                    {loading ? (
                      <>Saving...</>
                    ) : (
                      <>
                        <i className="fas fa-save"></i> Save Security Group
                      </>
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Styled with scoped JSX */}
      <style jsx>{`
        .settings-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 0;
          overflow-x: hidden;
        }

        .settings-header {
          background: rgba(15, 23, 42, 0.95);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(59, 130, 246, 0.2);
          padding: 1.5rem 2rem;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .header-content {
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .header-title h1 {
          font-size: 1.8rem;
          font-weight: 700;
          color: #60a5fa;
          margin: 0;
        }

        .header-title p {
          margin: 0.25rem 0 0;
          opacity: 0.8;
          font-size: 0.95rem;
        }

        .admin-info {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .admin-info strong {
          color: #60a5fa;
        }

        .settings-main {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        .settings-card {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 16px;
          padding: 2rem;
          border: 1px solid rgba(59, 130, 246, 0.3);
          transition: all 0.3s ease;
        }

        .settings-card:hover {
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.15);
        }

        .settings-grid {
          display: grid;
          grid-template-columns: ${isMobile ? "1fr" : "1fr 1.8fr"};
          gap: 2rem;
        }

        .info-panel {
          padding: 1.5rem;
          background: rgba(15, 23, 42, 0.6);
          border-radius: 14px;
          border: 1px solid rgba(59, 130, 246, 0.2);
        }

        .info-panel h3 {
          color: #60a5fa;
          margin-bottom: 1rem;
          font-size: 1.3rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .rules-list {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .rules-list li {
          margin-bottom: 0.6rem;
          padding-left: 1.2rem;
          position: relative;
        }

        .rules-list li::before {
          content: "✓";
          color: #10b981;
          position: absolute;
          left: 0;
        }

        .group-code-box {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          flex-wrap: wrap;
        }

        .group-code-box span {
          font-weight: 600;
        }

        .group-code-box input {
          padding: 0.7rem 1rem;
          border-radius: 10px;
          border: 1px solid rgba(59, 130, 246, 0.5);
          background: rgba(15, 23, 42, 0.8);
          color: #e2e8f0;
          font-size: 0.95rem;
          flex-grow: 1;
          min-width: 200px;
        }

        .group-code-box input:focus {
          outline: none;
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
        }

        .config-panel {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .ranks-header p {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          opacity: 0.9;
        }

        .add-rank-bar {
          display: flex;
          gap: 1rem;
          flex-direction: ${isMobile ? "column" : "row"};
        }

        .rank-select {
          flex: 1;
          padding: 0.9rem 1rem;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.5);
          background: rgba(15, 23, 42, 0.8);
          color: #e2e8f0;
          font-size: 1rem;
        }

        .rank-select:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .rank-select:focus {
          outline: none;
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
        }

        .add-btn {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          border: none;
          padding: 0.9rem 1.5rem;
          border-radius: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .add-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none !important;
          box-shadow: none !important;
        }

        .add-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3);
        }

        .selected-group h4 {
          color: #60a5fa;
          margin-bottom: 1rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .grades-list {
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-bottom: 1rem;
        }

        .grade-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-direction: ${isMobile ? "column" : "row"};
        }

        .grade-badge {
          background: #2563eb;
          color: white;
          padding: 0.7rem 1rem;
          border-radius: 10px;
          font-weight: 600;
          min-width: 120px;
          text-align: center;
        }

        .count-input {
          padding: 0.9rem 1rem;
          border-radius: 12px;
          border: 1px solid rgba(59, 130, 246, 0.5);
          background: rgba(15, 23, 42, 0.8);
          color: #e2e8f0;
          width: ${isMobile ? "100%" : "200px"};
        }

        .count-input:focus {
          outline: none;
          border-color: #60a5fa;
          box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
        }

        .remove-btn {
          background: #ef4444;
          color: white;
          border: none;
          width: 44px;
          height: 44px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .remove-btn:hover {
          background: #dc2626;
          transform: scale(1.1);
        }

        .submit-btn {
          background: linear-gradient(135deg, #f59e0b, #d97706);
          color: white;
          border: none;
          padding: 1rem;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 1rem;
        }

        .submit-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(245, 158, 11, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .settings-main {
            padding: 1rem;
          }
          .settings-header {
            padding: 1rem;
          }
          .header-content {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
}